import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Bars,
  CircleInfo,
  Grid2Plus,
} from '@unified-checkout/design-system/icons/index';
import { Tooltip } from '@unified-checkout/design-system';
import { useScreenWidth } from 'libs/design-system/src/hooks/useScreenWidth';

import type { IBreakdownProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer/breakdown';
import ButtonsGroup from '@app/dashboard/components/inner_layout/ButtonsGroup';
import { SMALL_MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { BREAK_DOWN_VIEWS } from '@app/dashboard/constants/balance_and_transfers/single-transfer';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';

import {
  getSharedEventProperties,
  handleStoreLastAction,
} from '../../../../utils/events-tracking';
import CompactView from './CompactView';
import DetailedView from './DetailedView';

import styles from './breakdown.module.scss';

const Breakdown = ({
  breakdown,
  isLoading,
  transferId,
  header,
}: IBreakdownProps) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState(BREAK_DOWN_VIEWS.COMPACT);

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= SMALL_MOBILE_BREAKPOINT;

  const handleChangeView = (view: string) => {
    const eventName =
      BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.BREAKDOWN_VIEW_TOGGLED;

    amplitude.track(eventName, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE.SELECTED_VIEW]:
        view,
      ...getSharedEventProperties({
        transferId: transferId ?? '',
        status: header?.status?.key ?? '',
        currency: header?.currency ?? '',
      }),
    });

    handleStoreLastAction(eventName);

    setActiveView(view);
  };

  return (
    <div className={styles['breakdown']}>
      <div className={styles['breakdown__header']}>
        <div className={styles['breakdown__header-content']}>
          <div className={styles['breakdown__header-content-title']}>
            <p className={styles['breakdown__header-content-title-text']}>
              {t('innerLayout.balanceTransfers.singleTransfer.breakdown.title')}
            </p>
            <Tooltip
              text={t(
                'innerLayout.balanceTransfers.singleTransfer.breakdown.tooltip'
              )}
            >
              {CircleInfo}
            </Tooltip>
          </div>
          <p className={styles['breakdown__header-content-subtitle']}>
            {t(
              'innerLayout.balanceTransfers.singleTransfer.breakdown.subtitle'
            )}
          </p>
        </div>
        <ButtonsGroup
          actions={[
            {
              leadingIcon: Grid2Plus,
              text: t(
                `innerLayout.balanceTransfers.singleTransfer.breakdown.views.${
                  isMobile ? 'compact' : 'compactView'
                }`
              ),
              onClickHandler: () => setActiveView('compact'),
              id: BREAK_DOWN_VIEWS.COMPACT,
              disabled: isLoading,
            },
            {
              leadingIcon: Bars,
              text: t(
                `innerLayout.balanceTransfers.singleTransfer.breakdown.views.${
                  isMobile ? 'detailed' : 'detailedView'
                }`
              ),
              onClickHandler: () => setActiveView('detailed'),
              id: BREAK_DOWN_VIEWS.DETAILED,
              disabled: isLoading,
            },
          ]}
          activeBtn={activeView}
          onActiveBtnChange={handleChangeView}
        />
      </div>
      <div className={styles['breakdown__content']}>
        {activeView === BREAK_DOWN_VIEWS.COMPACT ? (
          <CompactView breakdown={breakdown} isLoading={isLoading} />
        ) : (
          <DetailedView
            breakdown={breakdown}
            isLoading={isLoading}
            transferId={transferId}
            header={header}
          />
        )}
      </div>
    </div>
  );
};

export default Breakdown;
